import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import LineItems from "./LineItems";
import { computeHourlyRate } from "./functions";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format, parseISO, getDay } from "date-fns";
import { Mover } from "@/types";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export default function Billing() {
  const { watch, setValue, getValues } = useFormContext();
  const selectedMover = useSelector(getSelectedMover);
  const numMovers = watch("num_of_movers");
  const moverConfiguration = selectedMover?.mover_configuration;
  const [isRateSheetOpen, setIsRateSheetOpen] = useState(false);

  const changeBillingType = (value: string) => {
    setValue("billing_type", value);
    updateDefaultLineItem();
  };

  // the first line item is always added/updated depending on the billing type
  // this is so that we always have at least one line item
  const updateDefaultLineItem = () => {
    if (!moverConfiguration) {
      return;
    }

    const billingType = watch("billing_type");
    const currentLineItems = getValues("line_items") || [];
    const firstLineItem = currentLineItems[0];
    const laborRates = selectedMover?.labor_rates;
    const serviceDate = watch("service_date");

    if (billingType === "hourly") {
      let name, description;
      switch (selectedMover?.mover_configuration.hourly_rate_method) {
        case "standard_hourly":
          name = "Labor";
          // description = `${numMovers} movers per hour`;
          break;
        case "flat_plus_hourly":
          name = "Labor";
          description = `per billed hour`;
          break;
      }
      currentLineItems[0] = {
        id: firstLineItem.id,
        name: name,
        description: description,
        qty: null,
        price: computeHourlyRate(
          parseInt(numMovers),
          laborRates || [],
          serviceDate
        ),
        discount: 0,
        subtotal: null,
        mover_fee_id: null,
        type: "frozen",
        category: "labor",
        nature: "charge",
      };
    } else if (billingType === "flat_rate") {
      currentLineItems[0] = {
        id: firstLineItem.id,
        name: "Flat Rate",
        qty: 1,
        price: null,
        subtotal: null,
        mover_fee_id: null,
        type: "frozen",
        discount: 0,
        category: "flat",
        nature: "charge",
        description: null,
      };
    }

    setValue("line_items", currentLineItems);
  };

  if (!selectedMover) {
    return null;
  }

  return (
    <div className="relative">
      <div className="flex flex-row justify-between items-start">
        <div className="mb-6">
          <div className="text-sm font-medium mb-2">
            How is this move priced?
          </div>

          <RadioGroup
            defaultValue={watch("billing_type")}
            value={watch("billing_type")}
            onValueChange={changeBillingType}
            className="flex flex-col md:flex-row gap-4 max-w-xl"
          >
            <div className="w-full md:w-1/2">
              <RadioGroupItem
                value="hourly"
                id="billing-hourly"
                className="peer sr-only"
              />
              <Label
                htmlFor="billing-hourly"
                className="flex flex-row items-baseline gap-2 rounded-lg border border-stone-200 bg-popover px-4 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary peer-data-[state=checked]:bg-amber-50 [&:has([data-state=checked])]:bg-amber-50 cursor-pointer"
              >
                <div className="flex flex-col gap-0.5">
                  <div className="font-semibold">Hourly</div>
                  <p className="text-sm text-gray-800">
                    Labor charged at an hourly rate with additional fees
                  </p>
                </div>
              </Label>
            </div>

            <div className="w-full md:w-1/2">
              <RadioGroupItem
                value="flat_rate"
                id="billing-flat_rate"
                className="peer sr-only"
              />
              <Label
                htmlFor="billing-flat_rate"
                className="flex flex-row items-baseline gap-2 rounded-lg border border-stone-200 bg-popover px-4 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary peer-data-[state=checked]:bg-amber-50 [&:has([data-state=checked])]:bg-amber-50 cursor-pointer"
              >
                <div className="flex flex-col gap-0.5">
                  <div className="font-semibold">Flat Rate</div>
                  <p className="text-sm text-gray-800">
                    One flat rate for the job (also used on larger or long
                    distance moves)
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>
        <RateSheet
          mover={selectedMover}
          isRateSheetOpen={isRateSheetOpen}
          setIsRateSheetOpen={setIsRateSheetOpen}
          serviceDate={watch("service_date")}
          numMovers={watch("num_of_movers")}
        />
      </div>

      <LineItems mode="edit" buttonsPosition="top" />
    </div>
  );
}

const RateSheet = ({
  mover,
  isRateSheetOpen,
  setIsRateSheetOpen,
  serviceDate,
  numMovers,
}: {
  mover: Mover;
  isRateSheetOpen: boolean;
  setIsRateSheetOpen: (isOpen: boolean) => void;
  serviceDate: string;
  numMovers: number;
}) => {
  // Format currency for display
  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get day of week name for the service date
  const getServiceDayOfWeek = () => {
    if (!serviceDate) return null;

    const date =
      typeof serviceDate === "string" ? parseISO(serviceDate) : serviceDate;
    return format(date, "EEEE");
  };

  // Get day of week index (0-6, where 0 is Sunday)
  const getDayOfWeekIndex = () => {
    if (!serviceDate) return null;

    const date =
      typeof serviceDate === "string" ? parseISO(serviceDate) : serviceDate;
    return getDay(date); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  };

  // Determine which column should be highlighted based on day of week
  const getHighlightedColumn = () => {
    const dayIndex = getDayOfWeekIndex();
    if (dayIndex === null) return null;

    if (dayIndex === 0) return "sunday"; // Sunday
    if (dayIndex === 5) return "friday"; // Friday
    if (dayIndex === 6) return "saturday"; // Saturday
    return "mon_thu"; // Monday-Thursday
  };

  const highlightedColumn = getHighlightedColumn();

  return (
    <Sheet open={isRateSheetOpen} onOpenChange={setIsRateSheetOpen}>
      <SheetTrigger asChild>
        <span className="cursor-pointer text-sm font-semibold underline underline-offset-4 decoration-yellow-500">
          Table of Labor Rates
        </span>
      </SheetTrigger>
      <SheetContent className="w-full lg:max-w-lg">
        <SheetHeader className="mb-2">
          <SheetTitle>Hourly Labor Rates</SheetTitle>
          <SheetDescription className="text-gray-600 font-normal">
            <div className="text-sm">
              {getServiceDayOfWeek() && numMovers > 1 && (
                <span className="mt-1 font-medium">
                  Current move is a{" "}
                  <span className="text-black font-semibold">
                    {numMovers} mover
                  </span>{" "}
                  move on a{" "}
                  <span className="text-black font-semibold">
                    {getServiceDayOfWeek()}
                  </span>
                  .
                </span>
              )}
            </div>
          </SheetDescription>
        </SheetHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-0">Crew Size</TableHead>
                <TableHead>Mon-Thu</TableHead>
                <TableHead>Friday</TableHead>
                <TableHead>Saturday</TableHead>
                <TableHead>Sunday</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mover.labor_rates
                .filter((rate) => rate.crew_size > 1)
                .sort((a, b) => a.crew_size - b.crew_size)
                .map((rate) => (
                  <TableRow key={rate.crew_size}>
                    <TableCell className="font-medium pl-0">
                      {rate.crew_size === 0
                        ? "Additional Mover"
                        : `${rate.crew_size} ${
                            rate.crew_size === 1 ? "Mover" : "Movers"
                          }`}
                    </TableCell>
                    <TableCell
                      className={cn(
                        highlightedColumn === "mon_thu" &&
                          rate.crew_size === numMovers &&
                          "bg-amber-100 font-semibold"
                      )}
                    >
                      {formatCurrency(rate.mon_thu_rate)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        highlightedColumn === "friday" &&
                          rate.crew_size === numMovers &&
                          "bg-amber-100 font-semibold"
                      )}
                    >
                      {formatCurrency(rate.fri_rate)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        highlightedColumn === "saturday" &&
                          rate.crew_size === numMovers &&
                          "bg-amber-100 font-semibold"
                      )}
                    >
                      {formatCurrency(rate.sat_rate)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        highlightedColumn === "sunday" &&
                          rate.crew_size === numMovers &&
                          "bg-amber-100 font-semibold"
                      )}
                    >
                      {formatCurrency(rate.sun_rate)}
                    </TableCell>
                  </TableRow>
                ))}
              {/* Show additional mover rate last */}
              {mover.labor_rates
                .filter((rate) => rate.crew_size === 0)
                .map((rate) => (
                  <TableRow key={rate.crew_size}>
                    <TableCell className="font-medium pl-0">
                      Additional Mover
                    </TableCell>
                    <TableCell>{formatCurrency(rate.mon_thu_rate)}</TableCell>
                    <TableCell>{formatCurrency(rate.fri_rate)}</TableCell>
                    <TableCell>{formatCurrency(rate.sat_rate)}</TableCell>
                    <TableCell>{formatCurrency(rate.sun_rate)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          Note: These rates can be overridden on a per-move basis.
        </div>
      </SheetContent>
    </Sheet>
  );
};
